import { isAdrAvailable } from '@backstage-community/plugin-adr-common';
import { Entity } from '@backstage/catalog-model';
import { EntityLayout } from '@backstage/plugin-catalog';
import { EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import { useEntity } from '@backstage/plugin-catalog-react';
import { isTechDocsAvailable } from '@backstage/plugin-techdocs';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { LinearProgress } from '@mui/material';
import { hierachyProps } from '../../../redux/slices/hierachySlice';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import ADRList from '../../../pages/Adr';
import DomainOwnedTeamsEntity from '../../../pages/DomainOwnedTeamsEntity';
import { CustomDomainEntityLayout } from '../../../pages/EntityLayout/CustomDomainEntityLayout';
import GuardRails from '../../../pages/GuardRails';
import GuideLines from '../../../pages/GuideLines';
import JiraBoard from '../../../pages/JiraEpics/JiraBoard';
import CustomLinks from '../../../pages/Links';
import { ProductList } from '../../../pages/Products';
import { RolesListCard } from '../../../pages/RolesResponsibilities';
import { techdocsContent } from '../content/techdocsContent';
import TeamProductsList from '../../../pages/Products/TeamProductsList';
import {
  fetchData,
  urls,
} from '@internal/backstage-plugin-dh-dock-shared-common';
import useCustomEntities from '../../../hooks/useEntities';

interface DomainPageProps {
  entityWarningContent?: JSX.Element;
  name?: string;
  path?: string;
}

interface contactsTeamProps {
  link?: string;
  role?: string;
}

const useStyles = makeStyles({
  container: {
    marginTop: '1rem',
  },
  helpButton: {
    backgroundColor: '#05164d',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '25px',
    height: '25px',
    fontSize: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: '#0a2a80',
    },
  },
});
export function CopPage(props: DomainPageProps) {
  const { getCustomEntitiesByKind } = useCustomEntities('GroupInfo');
  const classes = useStyles();
  const path = window.location.pathname;
  const teamName = path?.split('/')[4];
  const { entity } = useEntity();
  const [productData,setProductData] =
    useState<Omit<hierachyProps, 'loading' | 'error'>>();
  const isDevSecOps =
    (path?.split('/')[3] === 'group' &&
      path?.split('/')[4] === 'dh_cop_copdevsecopsengineering') ||
    path?.split('/')[4] === 'dh_cop_dso';

  const [customEntity, setCustomEntity] = useState<Entity|any>();

  const [isLoading, setIsLoading] = useState(true);

  const updateLoading = (state: boolean) => {
    setIsLoading(state);
  };

  const getEntityByGroupInfo = useCallback(async () => {
    
    const resolvedGroupInfo = (await getCustomEntitiesByKind()).items;
    const getInfoByName = (await fetchData(
      `${urls.getTeamDataByTeamName}${teamName}`,
    )) as Entity;
    
    try {
      let entityResponse = resolvedGroupInfo.find(
        x =>
          x.spec?.entraGroupObjectId ===
         getInfoByName.metadata.annotations?.['graph.microsoft.com/group-id'],
      );
      
      if(entityResponse?.spec?.contactsTeam){
        const contactsTeamEntity = resolvedGroupInfo.find(
          x =>
            x.metadata.name?.trim()?.toLowerCase() ===
            entityResponse?.spec?.contactsTeam?.toString()?.toLowerCase()
        )?.spec?.userRoles as contactsTeamProps[];
        entityResponse.spec.contacts = contactsTeamEntity?.map(contact => ({
          link: contact.link ?? '',
          role: contact.role ?? '',
        }));
      }

      setCustomEntity(entityResponse);
      return entityResponse;
    } catch (error) {
      console.log(error);
      setCustomEntity(entity);
    }
    return null;
  }, [teamName]);

  const name = customEntity?.spec as any;
  const type = entity?.spec?.type;
  useEffect(() => {
  
      getEntityByGroupInfo();
    
  }, [teamName]);

  const baseUrl = `https://lhg-dh.crm4.dynamics.com/main.aspx?appid=1ac085ab-8208-f011-bae4-000d3a385b47&pagetype=entityrecord&etn=${name?.entityLogicalName ?? ''}&id=${customEntity?.metadata.name}`;

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      // The react-flow canvas is rendered dynamically, so we wait
      const canvas = containerRef.current?.querySelector<SVGSVGElement>(
        '.react-flow__renderer',
      );
      if (canvas) {
        canvas.style.transform = 'scale(0.5)'; // zoom out to 50%
        canvas.style.transformOrigin = '0 0';
        clearInterval(interval);
      }
    }, 100);
  }, []);
  return (
    <CustomDomainEntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        <Grid container spacing={3} alignItems="stretch">
          {props.entityWarningContent}
          <Grid item md={6} xs={12}>
            <Grid className={classes.container}>
              <RolesListCard
                title={
                  type?.toString()?.toLowerCase() === 'value stream'
                    ? 'VS Leadership'
                    : type?.toString()?.toLowerCase() === 'art'
                    ? 'ART Leadership'
                    : type?.toString()?.toLowerCase() === 'cop'
                    ? 'CoE Leadership'
                    : type?.toString()?.toLowerCase() === 'platform'
                    ? 'Platform Leadership'
                    : 'Contacts'
                }
                entity={customEntity}
                url={baseUrl}
              />
              <br />
              <CustomLinks groupInfoEntity={customEntity} url={baseUrl} />
              <br />
              <DomainOwnedTeamsEntity setProductData={setProductData} />
            </Grid>
          </Grid>
          <Grid item md={6} xs={12}>
            {!entity.metadata.name?.startsWith('dh_team_chapter') && (
              <>
                <Grid className={classes.container}>
                  <TeamProductsList
                    title=" Owning Products "
                    classes={classes.helpButton}
                    productData={productData}
                    entity={
                      customEntity ??
                      (getEntityByGroupInfo().then(res => {
                        return res;
                      }) as any)
                    }
                  />
                </Grid>
                <Grid className={classes.container}>
                  <ProductList
                    title="Contributing to Products"
                    classes={classes.helpButton}
                    type="team"
                    productData={productData}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </EntityLayout.Route>

      {isDevSecOps && (
        <EntityLayout.Route path="/epics" title="Epics">
          <JiraBoard />
        </EntityLayout.Route>
      )}
      {isDevSecOps && (
        <EntityLayout.Route path="/guidelines" title="GuideLines">
          <GuideLines />
        </EntityLayout.Route>
      )}
      {isTechDocsAvailable(entity) && (
        <EntityLayout.Route path="/docs" title="Docs">
          {techdocsContent}
        </EntityLayout.Route>
      )}
      {isAdrAvailable(entity) && (
        <EntityLayout.Route path="/adr" title="Adr">
          <ADRList />
        </EntityLayout.Route>
      )}
      {entity?.metadata.annotations?.['backstage.io/guardrail-location'] && (
        <EntityLayout.Route path="/guardRails" title="GuardRails">
          <>
            {isLoading && <LinearProgress />}
            {
              <GuardRails
                setIsLoading={updateLoading}
                url={
                  entity?.metadata.annotations?.[
                    'backstage.io/guardrail-location'
                  ]
                }
              />
            }
          </>
        </EntityLayout.Route>
      )}
      <EntityLayout.Route path="/graph" title="Graph">
        <div ref={containerRef}>
          <EntityCatalogGraphCard
            maxDepth={3}
            height={600}
            relations={['childOf', 'parentOf', 'ownerOf', 'contributesTo']}
            entityFilter={(entity: Entity) => {
              if (entity.kind === 'Group') {
                return (
                  ![
                    'dh_domain_techcop',
                    'dh_digitalhangarpeopleappgroups',
                  ].includes(entity.metadata.name.toLowerCase()) &&
                  !entity.metadata.name.toLowerCase().endsWith('contributors')
                );
              }

              if (entity.kind === 'System') {
                return true;
              }
              return false;
            }}
          />
        </div>
      </EntityLayout.Route>
    </CustomDomainEntityLayout>
  );
}
